/**
 * The "Check out as Guest" step of Easy Quote: a customer who isn't signed in opens a quote from
 * a link (`/easy-quote/?quoteId=<id>`), proves they own it with their customer number and sold-to
 * ZIP code, and is handed to the store's guest checkout. Rendered by
 * `EasyQuote.Components.tsx` → `EasyQuoteSteps` for the `"easy-quote"` step.
 *
 * Flow (`handleSubmit`, then the `authSession` effect):
 * 1. Both fields are checked with `EasyQuote.Functions.tsx` → `checkAllInputsValid`.
 * 2. `POST QuoteApi.PostPublicQuoteSession` (`/v1/quotes/v1/public/quote-session`,
 *    `web-quotes-api/application/src/functions/PostPublicQuoteSessionV1/postPublicQuoteSession.ts`)
 *    with `{ quoteId, customerNumber, soldToZip }`. The Quotes API answers 404 while the
 *    `10051-easy-quote` toggle is off, and 400 for a sold-to ZIP that doesn't match the quote, an
 *    expired or blocked quote, or a credit-risk customer. It returns `{ sessionToken, quote }`.
 * 3. On success, sessionStorage `authSessionQuote` gets `{ quoteId, quote }`, and
 *    `useApplication().setAuthSession` stores `{ token, type: "easy-quote", redirectUrl:
 *    "/easy-quote/?quoteId=<id>", enableRedirect: true }` in sessionStorage `authSession`.
 * 4. The effect sees an `easy-quote` `authSession`, `authSessionQuote` and no signed-in user, and
 *    replaces the location with `/store/easy-quote/`, a full load served by web-store-ui.
 * Any refusal shows the same "couldn't find any matching results" message with the brand's email
 * address and, when the error body has one, its `requestId`.
 *
 * Rules a change must keep (a contract with web-store-ui; see the "Easy Quote session" row of
 * `web-store-ui/STANDARDS.md`):
 * - `authSessionQuote` and its `{ quoteId, quote }` shape are read by
 *   `web-store-ui/application/src/components/EasyQuote/Context/EasyQuoteContext.tsx` (key in
 *   `EasyQuote/Helper/PageSupport/EasyQuote.models.ts` → `SessionItems.AuthSessionQuote`). A
 *   rename must change both repos.
 * - The guest's only credential is the session token. `@ryerson/frontend.application`'s axios
 *   interceptor (`Authentication.tsx` → `setupAxiosInterceptors`) sends it as `x-session-token`
 *   on requests whose path contains "session", only while nobody is signed in. On a 401 it clears
 *   `authSession` and sends the browser to `redirectUrl`, back to this page for the same quote.
 *   So `redirectUrl` must stay a site-root Easy Quote URL.
 * - Never log the session token or the error payload's request (web-architecture ADR-0015
 *   amendment). This file logs nothing.
 *
 * `AuthSessionType` and `AuthSession` below are local copies of the types that
 * `@ryerson/frontend.application` now exports (`Models.ts`); they must keep the same shape, since
 * the object goes straight into that package's `setAuthSession`.
 */
import React from "react";
import { useRouter } from "next/router";
import { useTheme } from "@ryerson/frontend.theme";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Button } from "@ryerson/frontend.button";
import { AllowedCharacters, Input, ZipCodeInput } from "@ryerson/frontend.form-controls";
import { useApplication } from "@ryerson/frontend.application";
import { EasyQuoteContent } from "./EasyQuoteContent";
import { QuoteApi } from "@ryerson/common.service-contracts";
import {
	checkAllInputsValid,
	validateCustomerNumber,
	axiosPOST,
	validatePostalCode,
} from "./EasyQuote.Functions";
import { EasyQuoteHeader, EasyQuoteErrorMessage } from "./EasyQuote.Components";
import { css } from "@emotion/react";
import { DisplaySteps } from "./EasyQuote.types";

/** Props of {@link EasyQuoteGuestForm}. `setDisplayStep` is used by Back, to the launcher. */
export interface EasyQuoteGuestFormProps {
	isMobile: boolean;
	setDisplayStep: React.Dispatch<React.SetStateAction<DisplaySteps>>;
}

/** The Easy Quote guest form; see the file header. */
export const EasyQuoteGuestForm: React.FC<EasyQuoteGuestFormProps> = ({
	isMobile,
	setDisplayStep,
}) => {
	const { theme } = useTheme();
	const router = useRouter();
	const quoteId = router.query.quoteId as string;
	// Passed to `axiosPOST`, which skips its callbacks when it is true. Nothing here sets it.
	const unMounted = React.useRef<boolean>(false);
	const {
		localization: { language },
		axiosInstance,
		setAuthSession,
		authSession,
		user: { isLoggedIn },
	} = useApplication();

	const content = EasyQuoteContent[language] || EasyQuoteContent.en;

	// Form State
	const [customerNumber, setCustomerNumber] = React.useState<string>("");
	// The quote's sold-to ZIP code, sent as `soldToZip`
	const [zipCode, setZipCode] = React.useState<string>("");
	const [submitted, setSubmitted] = React.useState<boolean>(false);

	// Error/Success State
	const [hasPageError, setHasPageError] = React.useState<boolean>(false);
	const [errorType, setErrorType] = React.useState<"validationError" | "apiError" | null>(null);
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
	const [correlationId, setCorrelationId] = React.useState<string>("");

	// Auto-clear errors when form becomes valid
	React.useEffect(() => {
		if (hasPageError) {
			const allValid = checkAllInputsValid(customerNumber, zipCode);
			if (allValid) {
				setHasPageError(false);
				clearMessage();
			}
		}
	}, [customerNumber, zipCode]); // Only trigger when form values change, not when error state changes

	const clearMessage = () => {
		setIsSubmitting(false);
		setHasPageError(false);
		setErrorType(null);
	};

	// Local copies of `@ryerson/frontend.application` → `AuthSessionType` / `AuthSession`; keep
	// them identical (see the file header).
	enum AuthSessionType {
		EASY_QUOTE = "easy-quote",
	}
	interface AuthSession {
		token: string;
		type: AuthSessionType;
		redirectUrl: string;
		enableRedirect?: boolean; // Controls whether to redirect on 401, defaults to true
	}

	// Hands the guest to the store once the session is set (step 4 in the file header). It also
	// runs when this step mounts, so a tab that still holds an `easy-quote` `authSession` and
	// `authSessionQuote` goes to `/store/easy-quote/` with the stored quote, without a request.
	React.useEffect(() => {
		const hasQuoteInSession = sessionStorage.getItem("authSessionQuote") !== null;
		// Only redirect to guest easy-quote flow if user is NOT logged in and quote data exists in session
		if (
			!isLoggedIn &&
			!!authSession &&
			authSession.type === AuthSessionType.EASY_QUOTE &&
			hasQuoteInSession
		) {
			window.location.replace(`/store/easy-quote/`);
		}
	}, [authSession, isLoggedIn]);

	const handleSubmit = () => {
		setSubmitted(true);

		// Check if form is valid using validation function
		const isValid = checkAllInputsValid(customerNumber, zipCode);

		if (isValid) {
			const requestBody: QuoteApi.PostPublicQuoteSessionRequest = {
				quoteId: quoteId || "",
				customerNumber,
				soldToZip: zipCode.trim(),
			};
			const apiUrl = QuoteApi.PostPublicQuoteSession.getUrl();

			axiosPOST<
				QuoteApi.PostPublicQuoteSessionRequest,
				QuoteApi.PostPublicQuoteSessionResponse
			>({
				apiUrl,
				axiosInstance,
				requestBody,
				unMounted, // Track if component is unmounted
				successCB: ({ sessionToken, quote }) => {
					// write to session storage the quoteId and the quote from response
					// for use in the store app after redirect
					// (read by web-store-ui `EasyQuote/Context/EasyQuoteContext.tsx`). It is
					// written before the token check; without a token the effect doesn't redirect.
					sessionStorage.setItem("authSessionQuote", JSON.stringify({ quoteId, quote }));

					if (sessionToken && quote) {
						let newAuthSession: AuthSession = {
							token: sessionToken,
							type: AuthSessionType.EASY_QUOTE,
							redirectUrl: `/easy-quote/?quoteId=${quoteId}`,
							enableRedirect: true,
						};

						setAuthSession(newAuthSession);
					}
				},
				errorCB: (errorPayload) => {
					// Extract requestId from consistent error payload structure
					// (the Quotes API's `awsHandler` error body is `{ errorMessage, requestId }`)
					const requestId = errorPayload.data?.requestId;

					requestId && setCorrelationId(requestId);
					setErrorType("apiError");
					setHasPageError(true);
				},
				isFetching: [isSubmitting, setIsSubmitting],
			});
		} else {
			// Show validation error
			setErrorType("validationError");
			setHasPageError(true);
		}
	};

	return (
		<Flex
			dataTestId="easy-quote-container"
			className="easy-quote-container"
			direction="column"
			justifyContent="space-evenly"
			css={{
				width: "100%",

				padding: isMobile ? "0px 0px 35px 0px" : "0px 0px 0px 0px",
				pointerEvents: isSubmitting ? "none" : "auto",
			}}
			onClick={!hasPageError ? clearMessage : undefined}
		>
			<EasyQuoteHeader content={content} isMobile={isMobile} hasPageError={hasPageError} />

			<EasyQuoteErrorMessage
				hasPageError={hasPageError}
				errorType={errorType}
				content={content}
				correlationId={correlationId}
				isMobile={isMobile}
			/>

			{/* Form inputs for Customer Number and Zip Code */}
			<Flex
				className="easy-quote-form-wrapper"
				direction="column"
				css={{ gap: 26, width: "100%" }}
			>
				<FlexItem>
					<Input
						maxLength={10}
						name="customerNumber"
						dataTestId="easy-quote-customer-number-input"
						type="primary"
						label={content.customerNumberLabel}
						value={customerNumber}
						onChange={(e) => setCustomerNumber(e.target.value.trim())}
						required={true}
						submitted={submitted}
						language={language}
						allowedCharacters={AllowedCharacters.Numeric}
						isValid={submitted ? validateCustomerNumber(customerNumber) : true}
					/>
				</FlexItem>

				<FlexItem css={{ width: "100%" }}>
					<ZipCodeInput
						dataTestId="easy-quote-zip-code-input"
						type="primary"
						label={content.soldToZipCodeLabel}
						value={zipCode}
						onChange={(e) => setZipCode(e.target.value)}
						required={true}
						submitted={submitted}
						language={language}
						isValid={submitted ? validatePostalCode(zipCode) : true}
					/>
				</FlexItem>

				<Flex
					direction={isMobile ? "column" : "row"}
					justifyContent="center"
					style={css`
						gap: 20px;
						width: 100%;
					`}
				>
					<Button
						dataTestId="easy-quote-back-button"
						css={css`
							width: ${isMobile ? "100%" : "160px"};
						`}
						label={content.back}
						onClick={() => {
							setDisplayStep(() => "launcher");
						}}
						background={theme.colors.primary.darkGray}
						foreground="light"
						fullwidth={true}
					/>

					<Button
						css={css`
							width: ${isMobile ? "100%" : "160px"};
						`}
						fullwidth={true}
						dataTestId="easy-quote-submit-button"
						label={content.continue}
						type="primary"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							handleSubmit();
						}}
						disabled={isSubmitting}
					/>
				</Flex>
			</Flex>
		</Flex>
	);
};
